import {
  dateLabel,
  growthReason,
  growthActionLabel,
  growthPriorityLabel,
  growthStatusLabel,
  growthEmailLabel,
} from "@/lib/usGrowth";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { isAxiosError } from "axios";
import { ChevronDown, ChevronUp, ExternalLink, Loader2 } from "lucide-react";
import {
  getGrowthDetail,
  prepareGrowthBouncer,
  reviewGrowth,
  type GrowthDetail,
  type GrowthFounder,
  type GrowthPriority,
  type GrowthReviewField,
} from "@/api/usGrowth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { UsGrowthScoreCard } from "./UsGrowthScore";
import { UsGrowthAssessment } from "./UsGrowthAssessment";

export function growthError(error: unknown): string {
  if (isAxiosError(error)) {
    if (error.response?.status === 403)
      return "Vos permissions ne permettent pas cette action ou la consultation de ces coordonnées.";
    if (error.response?.status === 409)
      return "Le dossier a changé ou cette action est bloquée. Actualisez, puis vérifiez les preuves, exclusions et préparations existantes.";
    if (error.response?.status === 404)
      return "Ce dossier US Growth est indisponible ou archivé.";
    if ([400, 422].includes(error.response?.status ?? 0))
      return "Vérifiez la valeur, la source et la date de la preuve. Une correction ne peut pas modifier un verdict technique.";
  }
  return "Le dossier n’a pas pu être chargé ou enregistré. Réessayez.";
}
export function PriorityBadge({ priority }: { priority: GrowthPriority }) {
  return (
    <Badge
      variant={priority === "BLOCKED" ? "destructive" : "outline"}
      className={
        priority === "QUICK_WIN"
          ? "border-emerald-200 bg-emerald-50 text-emerald-700"
          : ""
      }
    >
      {growthPriorityLabel(priority)}
    </Badge>
  );
}
function SafeLink({ href, children }: { href: unknown; children: ReactNode }) {
  if (typeof href !== "string" || !/^https?:\/\//i.test(href))
    return <span>{children}</span>;
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex max-w-full items-center gap-1 break-all text-indigo-600 hover:underline"
    >
      {children}
      <ExternalLink className="h-3 w-3 shrink-0" />
    </a>
  );
}
function Audit({ value }: { value: unknown }) {
  return (
    <details className="mt-4 text-xs text-slate-500">
      <summary className="cursor-pointer">
        Données source et traçabilité
      </summary>
      <pre className="mt-2 max-h-64 overflow-auto whitespace-pre-wrap break-all rounded bg-slate-50 p-3">
        {JSON.stringify(value, null, 2)}
      </pre>
    </details>
  );
}
function Evidence({ items }: { items: Record<string, unknown>[] }) {
  if (!items.length)
    return <p className="text-sm text-slate-500">Aucune preuve enregistrée.</p>;
  return (
    <ul className="space-y-3">
      {items.map((e, i) => (
        <li key={i} className="text-sm">
          <SafeLink href={e.url}>
            {typeof e.url === "string"
              ? e.url.replace(/^https?:\/\//, "")
              : String(e.source ?? "Preuve")}
          </SafeLink>
          <p className="mt-1 text-xs text-slate-500">
            {typeof e.observed_at === "string"
              ? dateLabel(e.observed_at)
              : "Date non documentée"}{" "}
            · preuve historique
          </p>
          {e.excerpt ? (
            <p className="mt-1 text-slate-600">{String(e.excerpt)}</p>
          ) : null}
        </li>
      ))}
    </ul>
  );
}

const reviewFields: [GrowthReviewField, string][] = [
  ["identity_status", "Identité"],
  ["affiliation_status", "Affiliation"],
  ["current_role", "Rôle actuel"],
  ["current_company", "Entreprise actuelle"],
  ["email_attribution_status", "Attribution de l’email"],
  ["email", "Nouvelle adresse candidate"],
  ["eligibility", "Éligibilité startup"],
  ["note", "Note de revue / contradiction"],
];
const statuses: Partial<Record<GrowthReviewField, string[]>> = {
  identity_status: ["CONFIRMED", "UNRESOLVED", "CONTRADICTED"],
  affiliation_status: [
    "CONFIRMED",
    "UNKNOWN",
    "FORMER_FOUNDER",
    "CONTRADICTED",
  ],
  email_attribution_status: ["CONFIRMED", "NOT_ATTRIBUTED", "CONTRADICTED"],
  eligibility: ["ADMISSIBLE", "REVIEW", "BLOCKED"],
};

function ReviewForm({
  slug,
  companyId,
  founder,
  onSaved,
}: {
  slug: string;
  companyId: number;
  founder: GrowthFounder;
  onSaved: (data: GrowthDetail) => void;
}) {
  const blocked = founder.priority === "BLOCKED";
  const [field, setField] = useState<GrowthReviewField>(
    blocked ? "note" : "identity_status",
  );
  const [value, setValue] = useState("");
  const [url, setUrl] = useState("");
  const [when, setWhen] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  async function submit(event: FormEvent) {
    event.preventDefault();
    setBusy(true);
    setError("");
    try {
      onSaved(
        await reviewGrowth(slug, companyId, founder.id, {
          request_id: crypto.randomUUID(),
          expected_revision: founder.revision,
          field,
          value,
          source_url: url,
          evidence_at: new Date(when).toISOString(),
          reason,
        }),
      );
      setValue("");
      setReason("");
    } catch (e) {
      setError(growthError(e));
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-3">
      <h5 className="text-sm font-medium">
        Enregistrer une correction sourcée
      </h5>
      <form onSubmit={submit} className="mt-3 space-y-3">
        <p className="text-xs text-slate-500">
          La correction conserve la source et l’auteur. Une nouvelle adresse
          remet son attribution et son contrôle technique à vérifier. Aucun
          statut Bouncer ou commercial n’est validé par cette action.
        </p>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label>Champ</Label>
            <Select
              value={field}
              onValueChange={(v) => {
                setField(v as GrowthReviewField);
                setValue("");
              }}
            >
              <SelectTrigger aria-label="Champ à corriger">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {reviewFields
                  .filter(([f]) => !blocked || f === "note")
                  .map(([f, name]) => (
                    <SelectItem key={f} value={f}>
                      {name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label htmlFor={`value-${founder.id}`}>Valeur</Label>
            {statuses[field] ? (
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger aria-label="Nouvelle valeur">
                  <SelectValue placeholder="Choisir un état" />
                </SelectTrigger>
                <SelectContent>
                  {statuses[field]?.map((s) => (
                    <SelectItem key={s} value={s}>
                      {growthStatusLabel(s)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                id={`value-${founder.id}`}
                type={field === "email" ? "email" : "text"}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                required
                maxLength={500}
              />
            )}
          </div>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor={`url-${founder.id}`}>URL de la preuve</Label>
            <Input
              id={`url-${founder.id}`}
              type="url"
              placeholder="https://…"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              required
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor={`when-${founder.id}`}>Date d’observation</Label>
            <Input
              id={`when-${founder.id}`}
              type="datetime-local"
              value={when}
              onChange={(e) => setWhen(e.target.value)}
              required
            />
          </div>
        </div>
        <div className="space-y-1">
          <Label htmlFor={`reason-${founder.id}`}>
            Preuve et justification
          </Label>
          <Textarea
            id={`reason-${founder.id}`}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            required
            minLength={10}
            maxLength={2000}
            placeholder="Ce que la source établit, et les éventuelles réserves…"
          />
        </div>
        {error && (
          <p role="alert" className="text-xs text-red-600">
            {error}
          </p>
        )}
        <Button
          type="submit"
          size="sm"
          disabled={
            busy || !value || !url || !when || reason.trim().length < 10
          }
        >
          {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Enregistrer
          la correction
        </Button>
      </form>
    </section>
  );
}

function FounderCard({
  slug,
  companyId,
  founder: f,
  canReview,
  onSaved,
}: {
  slug: string;
  companyId: number;
  founder: GrowthFounder;
  canReview: boolean;
  onSaved: (data: GrowthDetail) => void;
}) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [preparing, setPreparing] = useState(false);
  const [error, setError] = useState("");
  async function prepare() {
    setPreparing(true);
    setError("");
    try {
      onSaved(
        await prepareGrowthBouncer(slug, companyId, f, crypto.randomUUID()),
      );
    } catch (e) {
      setError(growthError(e));
    } finally {
      setPreparing(false);
    }
  }
  const blocked = f.priority === "BLOCKED";
  return (
    <article className="rounded-xl border border-slate-200 bg-white">
      <div className="p-4">
        <header className="flex items-start justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <div
              aria-hidden="true"
              className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600"
            >
              {f.name
                .split(" ")
                .filter(Boolean)
                .slice(0, 2)
                .map((n) => n[0])
                .join("")}
            </div>
            <div className="min-w-0">
              <h4 className="font-semibold text-slate-900">{f.name}</h4>
              <p className="mt-0.5 text-xs text-slate-500">
                {growthStatusLabel(f.current_role)}
                {f.current_company && f.current_company !== "UNKNOWN"
                  ? ` · ${f.current_company}`
                  : ""}
              </p>
            </div>
          </div>
          <Badge
            variant={blocked ? "destructive" : "outline"}
            className="shrink-0"
          >
            {blocked
              ? "Bloqué"
              : f.verified_on_merits
                ? "Vérifié sur le fond"
                : "À vérifier"}
          </Badge>
        </header>
        <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
          <span className="break-all text-slate-700">
            {f.hidden_details.length
              ? "Email masqué"
              : f.email || "Email manquant"}
          </span>
          {f.email && (
            <span
              className={`text-xs ${f.bouncer_status === "DELIVERABLE" && f.bouncer_fresh ? "text-emerald-700" : "text-amber-700"}`}
            >
              {growthEmailLabel(f.bouncer_status, f.bouncer_fresh)}
            </span>
          )}
        </div>
        <div className="mt-3 flex items-center justify-between gap-3 border-t border-slate-100 pt-3">
          <p className="text-xs text-slate-600">
            <span className="font-medium text-slate-800">À faire : </span>
            {growthActionLabel(f.next_action)}
          </p>
          <Button
            size="sm"
            variant="ghost"
            className="h-8 shrink-0 px-2"
            aria-expanded={open}
            aria-controls={`founder-${f.id}`}
            onClick={() => {
              setOpen(!open);
              setEditing(false);
            }}
          >
            {open ? "Fermer" : "Détails"}
            {open ? (
              <ChevronUp className="ml-1 h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="ml-1 h-3.5 w-3.5" />
            )}
          </Button>
        </div>
      </div>
      {open && (
        <div
          id={`founder-${f.id}`}
          className="border-t border-slate-100 px-4 pb-4 pt-3"
        >
          <Tabs defaultValue="review">
            <TabsList className="mb-4 flex h-auto w-full flex-wrap justify-start gap-1 bg-slate-50">
              <TabsTrigger value="review">À vérifier</TabsTrigger>
              <TabsTrigger value="email">Email</TabsTrigger>
              <TabsTrigger value="evidence">Preuves</TabsTrigger>
              <TabsTrigger value="history">Historique</TabsTrigger>
            </TabsList>
            <TabsContent value="review" className="space-y-4 text-sm">
              <div className="space-y-1 text-slate-600">
                <p>
                  <span className="font-medium text-slate-900">
                    Identité :{" "}
                  </span>
                  {growthStatusLabel(f.identity_status)}
                  {f.linkedin && (
                    <>
                      {" "}
                      · <SafeLink href={f.linkedin}>LinkedIn</SafeLink>
                    </>
                  )}
                </p>
                <p>
                  <span className="font-medium text-slate-900">
                    Affiliation :{" "}
                  </span>
                  {growthStatusLabel(f.affiliation_status)} · {f.target_company}
                </p>
                <p className="text-xs">
                  Rôle historique : {growthStatusLabel(f.historical_role)} ·
                  dernier contrôle : {dateLabel(f.last_checked_at)}
                </p>
              </div>
              <div>
                <p className="mb-2 font-medium text-slate-900">Ce qui manque</p>
                {f.blockers.length ? (
                  <ul className="list-disc space-y-1 pl-4 text-slate-600">
                    {[...new Set(f.blockers.map(growthReason))].map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-slate-500">
                    Aucun obstacle documenté sur le fond.
                  </p>
                )}
              </div>
              {!!f.contradictions.length && (
                <div className="rounded-lg bg-amber-50 p-3 text-amber-900">
                  <p className="font-medium">Preuves contradictoires</p>
                  {f.contradictions.map((c, i) => (
                    <p key={i} className="mt-1">
                      {c}
                    </p>
                  ))}
                </div>
              )}
              <p className="text-xs text-slate-500">
                Éligibilité société : {growthStatusLabel(f.eligibility)}.{" "}
                {f.campaign_eligible
                  ? "Admissible selon les contrôles enregistrés."
                  : "Prospection en attente des contrôles commerciaux."}
              </p>
            </TabsContent>
            <TabsContent
              value="email"
              className="space-y-3 text-sm text-slate-600"
            >
              <p className="break-all font-medium text-slate-900">
                {f.hidden_details.length
                  ? "Coordonnées masquées selon vos permissions"
                  : f.email || "Aucune adresse professionnelle connue"}
              </p>
              <p>
                {f.email_attributed
                  ? "Adresse attribuée à cette personne"
                  : "Attribution personnelle à confirmer"}{" "}
                · {growthStatusLabel(f.email_company)}
              </p>
              <p>
                Verdict Bouncer :{" "}
                <strong className="text-slate-900">{f.bouncer_status}</strong> ·{" "}
                {dateLabel(f.bouncer_checked_at)}
              </p>
              <p className="text-xs">
                {f.bouncer_status === "ACCEPT_ALL"
                  ? "Le domaine accepte toutes les adresses : cette boîte n’est pas vérifiée."
                  : f.bouncer_fresh
                    ? "Contrôle technique dans les 30 jours."
                    : "Contrôle ancien ou absent : à actualiser avant utilisation."}
              </p>
              {!!f.alternative_emails.length && (
                <div>
                  <p className="mb-1 font-medium">Autres adresses connues</p>
                  <ul className="space-y-1">
                    {f.alternative_emails.map((e, i) => (
                      <li key={i} className="break-all">
                        {String(
                          e.email ??
                            e.address ??
                            "Adresse dans les données source",
                        )}{" "}
                        ·{" "}
                        {String(
                          e.bouncer_status ?? e.status ?? "Non contrôlée",
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              <Audit
                value={{
                  sources: f.email_sources,
                  alternatives: f.alternative_emails,
                  raw_verdict: f.bouncer_raw_status,
                }}
              />
              {canReview &&
                !blocked &&
                f.email &&
                f.email_attributed &&
                !f.bouncer_fresh && (
                  <details className="rounded-lg border border-slate-200 p-3">
                    <summary className="cursor-pointer font-medium text-slate-800">
                      Préparer un contrôle Bouncer
                    </summary>
                    <p className="mt-3 text-xs">
                      Bouncer · adresse prévue : {f.email} · estimation : 1
                      crédit · solde inconnu.
                    </p>
                    <p className="my-2 text-xs">
                      Préparation sans transmission ni dépense. L’exécution
                      exige une autorisation fournisseur et un budget.
                    </p>
                    {f.prepared_verification ? (
                      <Badge variant="outline">
                        Préparée · autorisation en attente
                      </Badge>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={preparing}
                        onClick={prepare}
                      >
                        {preparing && (
                          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        )}
                        Préparer sans dépense
                      </Button>
                    )}
                    {error && (
                      <p role="alert" className="mt-2 text-xs text-red-600">
                        {error}
                      </p>
                    )}
                  </details>
                )}
            </TabsContent>
            <TabsContent value="evidence">
              <Evidence items={f.evidence} />
              <Audit
                value={{
                  references: f.source_references,
                  person: f.canonical_person_uuid,
                  source_uuids: f.source_uuids,
                  missing_proofs: f.missing_proofs,
                  priority_reasons: f.priority_reasons,
                }}
              />
            </TabsContent>
            <TabsContent
              value="history"
              className="space-y-3 text-sm text-slate-600"
            >
              <p>
                <span className="font-medium text-slate-900">
                  Prospection :{" "}
                </span>
                {growthStatusLabel(f.commercial_status)}
              </p>
              <p>
                Oppositions et suppressions :{" "}
                {growthStatusLabel(f.suppression_status)}
              </p>
              <p>
                Conflit de campagne : {growthStatusLabel(f.campaign_conflict)}
              </p>
              {!!f.reviews.length ? (
                <ul className="space-y-3">
                  {f.reviews.map((r, i) => (
                    <li key={i} className="border-l-2 border-slate-200 pl-3">
                      <p className="text-xs text-slate-500">
                        {dateLabel(String(r.recorded_at ?? ""))} ·{" "}
                        {reviewFields.find(
                          ([field]) => field === r.field,
                        )?.[1] ?? "Revue"}
                      </p>
                      <p>
                        {String(r.value ?? r.state ?? "")} ·{" "}
                        {String(r.reason ?? "")}
                      </p>
                      <SafeLink href={r.source_url}>
                        Source de la correction
                      </SafeLink>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">
                  Aucune correction enregistrée depuis l’import.
                </p>
              )}
              <Audit
                value={{
                  outreach: f.outreach_history,
                  reviews: f.reviews,
                  historical_qualification: f.historical_qualification,
                }}
              />
            </TabsContent>
          </Tabs>
          {canReview && (
            <div className="mt-4 border-t border-slate-100 pt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setEditing(!editing)}
              >
                {editing
                  ? "Annuler la correction"
                  : blocked
                    ? "Ajouter une note de revue"
                    : "Enregistrer une correction"}
              </Button>
              {editing && (
                <ReviewForm
                  key={f.revision}
                  slug={slug}
                  companyId={companyId}
                  founder={f}
                  onSaved={(d) => {
                    onSaved(d);
                    setEditing(false);
                  }}
                />
              )}
            </div>
          )}
        </div>
      )}
    </article>
  );
}

export function UsGrowthDossier({
  slug,
  companyId,
  onChanged,
  variant = "examiner",
}: {
  slug: string;
  companyId: number;
  onChanged?: () => void;
  variant?: "company" | "examiner";
}) {
  const [data, setData] = useState<GrowthDetail | null>(null);
  const [error, setError] = useState("");
  const [version, setVersion] = useState(0);
  useEffect(() => {
    const controller = new AbortController();
    setData(null);
    setError("");
    getGrowthDetail(slug, companyId, controller.signal)
      .then(setData)
      .catch((e: unknown) => {
        if (!controller.signal.aborted) setError(growthError(e));
      });
    return () => controller.abort();
  }, [slug, companyId, version]);
  if (error)
    return (
      <div role="alert" className="my-4 text-sm text-red-700">
        {error}
        <Button
          variant="outline"
          size="sm"
          onClick={() => setVersion((v) => v + 1)}
          className="ml-2"
        >
          Réessayer
        </Button>
      </div>
    );
  if (!data)
    return (
      <div
        className="flex justify-center py-8"
        aria-label="Chargement du dossier"
      >
        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
      </div>
    );
  return (
    <div className={variant === "examiner" ? "mt-5 space-y-4" : "space-y-4"}>
      <div>
        {variant === "examiner" && (
          <h3 className="text-lg font-semibold text-slate-900">
            {data.company.name}
          </h3>
        )}
        <p className="mt-1 text-sm text-slate-500">
          {data.company.verified} founder{data.company.verified > 1 ? "s" : ""}{" "}
          vérifié{data.company.verified > 1 ? "s" : ""} sur le fond ·{" "}
          {data.company.remaining} à vérifier
        </p>
      </div>
      {variant === "examiner" && data.assessment && (
        <UsGrowthAssessment
          assessment={data.assessment}
          slug={slug}
          companyId={companyId}
          onSaved={(next) => {
            setData(next);
            onChanged?.();
          }}
        />
      )}
      {variant === "examiner" && !data.assessment && data.score && (
        <UsGrowthScoreCard score={data.score} />
      )}
      {data.founders.map((f) => (
        <FounderCard
          key={f.id}
          slug={slug}
          companyId={companyId}
          founder={f}
          canReview={data.can_review}
          onSaved={(next) => {
            setData(next);
            onChanged?.();
          }}
        />
      ))}
      <details className="text-xs text-slate-500">
        <summary className="cursor-pointer">
          Sources et contexte de la société
        </summary>
        <p className="mt-3">
          Éligibilité : {growthStatusLabel(data.company.eligibility)} ·
          financement : {growthStatusLabel(data.company.funding_stage)} ·
          dernier contrôle : {dateLabel(data.company.last_checked_at)}
        </p>
        <p className="mt-2">
          {data.company.historical_uuid_count} dossiers historiques ·{" "}
          {data.company.excluded_founders} exclus ·{" "}
          {data.company.excluded_aliases} alias
        </p>
        <p className="mt-2">
          {data.company.deliverable} emails DELIVERABLE (
          {data.company.reusable_deliverable} réutilisables techniquement) ·{" "}
          {data.company.accept_all} ACCEPT_ALL · {data.company.risky} RISKY ·{" "}
          {data.company.missing_emails} manquants ·{" "}
          {data.company.unchecked_emails} non contrôlés ·{" "}
          {data.company.unattributed_emails} non attribués
        </p>
        <p className="mt-2">
          {data.company.commercial_pending} contrôles commerciaux en attente.
        </p>
        <ul className="mt-2 space-y-1">
          {data.company.blockers.map((b) => (
            <li key={b}>{growthReason(b)}</li>
          ))}
        </ul>
        <Audit
          value={{
            canonical_company: data.company.canonical_company_uuid,
            source_uuids: data.company.source_company_uuids,
            evidence: data.company_evidence,
            priority: data.company.priority_reasons,
          }}
        />
      </details>
    </div>
  );
}
